/**
 * Central EventBus - Fully decoupled type-safe publish/subscribe bus.
 * Zero hard dependencies between modules.
 */

import {
  CombatHitEventData,
  ThreatChangeEventData,
  CastInterruptedEventData,
  LogMessage,
  ActiveBuff,
} from './Types';

export interface EventMap {
  COMBAT_HIT: CombatHitEventData;
  UNIT_DIED: { victimId: string; killerId?: string };
  THREAT_CHANGED: ThreatChangeEventData;
  CAST_STARTED: { entityId: string; spellName: string; castTime: number; targetId: string | null };
  CAST_INTERRUPTED: CastInterruptedEventData;
  HEAL_APPLIED: { sourceId: string; sourceName: string; targetId: string; targetName: string; amount: number; isCrit: boolean };
  BUFF_APPLIED: { targetId: string; buff: ActiveBuff };
  BUFF_REMOVED: { targetId: string; buffId: string; concReturned: number };
  CC_APPLIED: { targetId: string; ccType: 'stun' | 'mez' | 'root' | 'snare'; duration: number; sourceName: string };
  MEZ_BROKEN: { targetId: string; breakerName: string };
  RESOURCE_GATHERED: { harvesterId: string; resourceType: string; amount: number };
  ITEM_CRAFTED: { crafterId: string; itemKey: string; quality: string; discipline: string };
  FAME_CHANGED: { playerId: string; oldFame: number; newFame: number; delta: number };
  LOG_EMITTED: LogMessage;
  PROJECTILE_SPAWNED: {
    type: string;
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    speed?: number;
  };
  SPELL_EFFECT_TRIGGERED: {
    type: string;
    gx: number;
    gy: number;
    radius?: number;
    color?: string;
  };
  TARGET_CHANGED: { targetId: string | null };
  STICK_STATUS_CHANGED: { active: boolean; targetId: string | null; targetName?: string };
  RESOURCE_RESPAWNED: { nodeId: string; x: number; y: number; resourceType: string; name: string };
  COMBAT_STATE_CHANGED: { inCombat: boolean };
  THUNDER_STRIKE: { intensity?: number };
}

export type EventKey = keyof EventMap;
export type EventCallback<K extends EventKey> = (data: EventMap[K]) => void;

class EventBusInstance {
  private listeners: { [K in EventKey]?: Array<EventCallback<K>> } = {};

  public on<K extends EventKey>(event: K, callback: EventCallback<K>): () => void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    (this.listeners[event] as Array<EventCallback<K>>).push(callback);

    // Return unbind function
    return () => this.off(event, callback);
  }

  public off<K extends EventKey>(event: K, callback: EventCallback<K>): void {
    const list = this.listeners[event];
    if (!list) return;
    this.listeners[event] = list.filter((cb) => cb !== callback) as any;
  }

  public emit<K extends EventKey>(event: K, data: EventMap[K]): void {
    const list = this.listeners[event];
    if (!list || list.length === 0) return;
    for (let i = 0; i < list.length; i++) {
      try {
        list[i](data);
      } catch (err) {
        console.error(`[EventBus] Error in listener for event "${event}":`, err);
      }
    }
  }

  public clear(): void {
    this.listeners = {};
  }
}

export const EventBus = new EventBusInstance();
